import templateApi from 'api/templateApi'
import {
  DeleteTemplateRequest,
  WhatsAppTemplateSaveRequest,
  TemplateListResponse,
  TemplateListRequest,
  AdvertisementListResponse,
  AdvertisementSaveRequest,
  AdvertisementListRequest,
  UserTemplates,
  SmsListResponse,
  SmsTemplateSaveRequest
} from './types'

const templateApiSlice = templateApi.injectEndpoints({
  endpoints: (build) => ({
    getTemplates: build.query<TemplateListResponse, TemplateListRequest>({
      query: ({ filters, page }) => {
        const params: { [key: string]: any } = {
          size: page?.size ?? 10,
          page: page?.number ? page.number - 1 : 0
        }

        if (filters && filters.length > 0) {
          filters.forEach(({ fieldName, fieldValue }) => {
            if (
              fieldValue === null ||
              fieldValue === undefined ||
              fieldValue === '' ||
              fieldValue === 'all'
            ) {
              delete params[fieldName]
            } else {
              params[fieldName] = fieldValue
            }
          })
        }

        return {
          url: `/extended/templates`,
          method: 'GET',
          params
        }
      },
      providesTags: ['templateList']
    }),
    createTemplate: build.mutation<void, WhatsAppTemplateSaveRequest>({
      query: ({ whatsAppTemplateDto, file }) => {
        const formData = new FormData()
        formData.append(
          'whatsAppTemplateDto',
          new Blob([JSON.stringify(whatsAppTemplateDto)], {
            type: 'application/json'
          })
        )
        if (file instanceof File) {
          formData.append('file', file)
        }

        return {
          url: '/whatsapp/template',
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      },
      invalidatesTags: ['templateList']
    }),
    createAdvertisement: build.mutation<
      AdvertisementListResponse,
      AdvertisementSaveRequest
    >({
      query: ({ advertisementDTO, image, video }) => {
        const formData = new FormData()

        if (advertisementDTO) {
          const advertisementBlob = new Blob(
            [JSON.stringify(advertisementDTO)],
            {
              type: 'application/json'
            }
          )
          formData.append('advertisementDTO', advertisementBlob)
        }

        if (image && typeof image !== 'string') {
          formData.append('image', image)
        }

        if (video && typeof video !== 'string') {
          formData.append('video', video)
        }

        return {
          url: '/advertisement/extended',
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      },
      invalidatesTags: ['advertisement']
    }),
    fetchAdvertisementList: build.query<
      AdvertisementListResponse,
      AdvertisementListRequest
    >({
      query: ({ filters, page }) => {
        const params: { [key: string]: any } = {
          size: page?.size ?? 10,
          page: page?.number ? page.number - 1 : 0
        }

        if (filters && filters.length > 0) {
          filters.forEach(({ fieldName, fieldValue }) => {
            if (
              fieldValue === null ||
              fieldValue === undefined ||
              fieldValue === '' ||
              fieldValue === 'all'
            ) {
              delete params[fieldName]
            } else {
              params[fieldName] = fieldValue
            }
          })
        }

        return {
          url: '/advertisements/getAllInitiatedAdvertisements',
          method: 'GET',
          params
        }
      },
      providesTags: ['advertisement']
    }),
    fetchTemplateVerificationList: build.query<
      AdvertisementListResponse,
      AdvertisementListRequest
    >({
      query: ({ filters, page }) => {
        const params: { [key: string]: any } = {
          size: page?.size ?? 10,
          page: page?.number ? page.number - 1 : 0,
          channel: 'INAPP'
        }

        if (filters && filters.length > 0) {
          filters.forEach(({ fieldName, fieldValue }) => {
            if (
              fieldValue === null ||
              fieldValue === undefined ||
              fieldValue === '' ||
              fieldValue === 'all'
            ) {
              delete params[fieldName]
            } else {
              params[fieldName] = fieldValue
            }
          })
        }
        return {
          url: '/advertisements/in-app-by-initiate',
          method: 'GET',
          params
        }
      },
      providesTags: ['advertisement']
    }),
    createSmsTemplate: build.mutation<SmsListResponse, SmsTemplateSaveRequest>({
      query: (templateDTO) => ({
        url: `/msg91/templates`,
        method: 'POST',
        data: templateDTO
      }),
      invalidatesTags: ['advertisement']
    }),
    deleteAdvertisement: build.mutation<void, { id: any }>({
      query: ({ id }) => ({
        url: `/advertisements/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['advertisement']
    }),
    editAdvertisement: build.mutation<
      AdvertisementListResponse,
      AdvertisementSaveRequest
    >({
      query: ({ advertisementDTO, image, video }) => {
        const formData = new FormData()
        if (advertisementDTO) {
          const advertisementBlob = new Blob(
            [JSON.stringify(advertisementDTO)],
            {
              type: 'application/json'
            }
          )
          formData.append('advertisementDTO', advertisementBlob)
        }

        if (image && typeof image !== 'string') {
          formData.append('image', image)
        }

        if (video && typeof video !== 'string') {
          formData.append('video', video)
        }

        return {
          url: `/advertisement/extended/${advertisementDTO?.id}`,
          method: 'PATCH',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      },
      invalidatesTags: ['advertisement']
    }),
    updateTemplateApproval: build.mutation<
      AdvertisementListResponse,
      { userTemplates: UserTemplates, id: number }
    >({
      query: ({ id, userTemplates }) => ({
        url: `/advertisements/admin-template-verification/${id}`,
        method: 'PATCH',
        data: userTemplates
      }),
      invalidatesTags: ['advertisement']
    }),

    deleteTemplate: build.mutation<void, DeleteTemplateRequest>({
      query: ({ channelId, templateId, templateName }) => ({
        url: `/whatsapp/template?channelId=${channelId}&templateId=${templateId}&templateName=${templateName}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['templateList']
    })
  })
})

export const {
  useGetTemplatesQuery,
  useCreateAdvertisementMutation,
  useCreateTemplateMutation,
  useFetchAdvertisementListQuery,
  useDeleteTemplateMutation,
  useDeleteAdvertisementMutation,
  useEditAdvertisementMutation,
  useFetchTemplateVerificationListQuery,
  useUpdateTemplateApprovalMutation,
  useCreateSmsTemplateMutation
} = templateApiSlice

export default templateApiSlice
